import { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import Collapse from "../../common/Collapse";
import Button from "../../common/Button";
import SortableSlideItem from "./SortableSlideItem";
import GenerateVideoProgressBar from "./GenerateVideoProgressBar";
import { calculateEstimatedTime, formatEstimatedTime } from "../helpers";

export default function GenerateVideoCollapse({
  newsList,
  selectedVideoTemplate,
  selectedNews,
  setSelectedNews,
  styleSettings,
  onGenerateVideo,
  loading = false,
  progress = 0,
  disabled = false,
  isItemsLengthValid,
  itemsLengthRestriction,
}) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get the ordered news items based on selectedNews IDs
  const orderedNewsItems = useMemo(() => {
    return selectedNews
      .map((id) => newsList.find((news) => news.id === id))
      .filter(Boolean);
  }, [selectedNews, newsList]);

  // Calculate estimated time based on voice texts
  const estimatedTime = useMemo(() => {
    return calculateEstimatedTime(orderedNewsItems);
  }, [orderedNewsItems]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active?.id && over?.id && active.id !== over.id) {
      setSelectedNews((items) => {
        const oldIndex = items.findIndex((item) => item === active.id);
        const newIndex = items.findIndex((item) => item === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Collapse
      title={
        <div className="font-semibold text-gray-800 text-2xl">
          {t("generate video")}
        </div>
      }
      className="mt-6"
      disabled={disabled}
      defaultOpen={!disabled}
    >
      {orderedNewsItems.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-gray-400 text-6xl mb-4">🎬</div>
          <p className="text-gray-500 font-semibold mb-2">
            {t("no slides selected")}
          </p>
          <p className="text-sm text-gray-400">
            {t(
              "check news items from the content section to add them to your video"
            )}
          </p>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center gap-4 mb-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex-1 flex flex-col">
              <p className="text-sm text-blue-800">
                <strong>{t("playlist order")}:</strong>{" "}
                {t("drag and drop slides to reorder them")}
              </p>
              {estimatedTime > 0 && (
                <div className="text-xs">
                  {t("estimated video duration")}:{" "}
                  <span className="font-semibold">
                    {formatEstimatedTime(estimatedTime)}
                  </span>
                </div>
              )}
              {!isItemsLengthValid && (
                <div className="text-xs text-red-500">
                  {t("items length")}: {orderedNewsItems.length} /{" "}
                  {itemsLengthRestriction || "∞"}
                </div>
              )}
            </div>
            <Button
              color="inverse_primary"
              onClick={onGenerateVideo}
              type="button"
              loading={loading}
              disabled={
                orderedNewsItems.length === 0 || loading || !isItemsLengthValid
              }
            >
              {t("generate video")}
            </Button>
          </div>
          {loading && progress > 0 && (
            <div className="mb-4">
              <GenerateVideoProgressBar progress={progress} />
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedNews}
              strategy={rectSortingStrategy}
              disabled={loading}
            >
              <div
                className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {orderedNewsItems.map((news, index) => (
                  <SortableSlideItem
                    key={news.id}
                    news={news}
                    styleSettings={styleSettings}
                    index={index}
                    selectedVideoTemplate={selectedVideoTemplate}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}
    </Collapse>
  );
}
